import { IndoorEqualSpritesURL } from "./services";
import { createWebComp } from "./widgets";

export const DISABLED_LEVEL = "-1000";
const LEVEL_SEM_LAYER = "levels";


export function isIndoorEqualAvailable() {
	/* eslint-disable no-undef */
	try {
		IndoorEqual;
		return true;
	}
	catch(e) {
		return false;
	}
}

/**
 * Enables indoor= plugin on MapLibre
 * @param {Map} map The MapLibre map
 * @param {boolean} [forceSwitchOn=false] Set to true to skip init options check for default visibility
 */
export function initIndoorEqualOnMap(map, forceSwitchOn = false) {
	if(map._options.indoor) {
		if(isIndoorEqualAvailable()) {
			if(!forceSwitchOn && map._options.indoor.visible === false) {
				return;
			}

			map.indoorEqual = new IndoorEqual(map, map._options.indoor);
			map.addControl(map.indoorEqual);
			map.indoorEqual.loadSprite(IndoorEqualSpritesURL());

			// Override level refresh to keep "disabled" state after map move
			map.indoorEqual._refreshAfterLevelsUpdate = function() {
				if (!this.levels.includes(this.level) && this.level != DISABLED_LEVEL) {
					this.setLevel("0");
				}
			};

			// Override updateLevels to take into account pictures levels
			const SEM_SRC_ID = "sem-"+LEVEL_SEM_LAYER;
			map.indoorEqual._updateLevels = function() {
				if(
					this.map.getSource(this.source.sourceId)
					&& this.map.isSourceLoaded(this.source.sourceId)
					&& this.map.getSource(SEM_SRC_ID)
					&& this.map.isSourceLoaded(SEM_SRC_ID)
				) {
					let levels = new Set();

					// Read levels from indoor= tiles
					this.map.querySourceFeatures(this.source.sourceId, { sourceLayer: "area" })
						.forEach(f => {
							if(f.properties.class === "level") { return; }
							if(f.properties.level !== undefined) {
								levels.add(f.properties.level.toString());
							}
						});
					
					// Read levels from Panoramax tiles
					this.map.querySourceFeatures(SEM_SRC_ID, { sourceLayer: "layer" })
						.forEach(f => {
							if(f.properties["osm|level"] !== undefined) {
								levels.add(f.properties["osm|level"].toString());
							}
						});
					
					// If at least one level exist, add floor just to be sure it's always present
					if(levels.size > 0) { levels.add("0"); }

					if(levels.symmetricDifference(new Set(this.levels)).size !== 0) {
						this.levels = [...levels].sort((a, b) => parseFloat(a) - parseFloat(b)).reverse();
						this._emitLevelsChange();
						this._refreshAfterLevelsUpdate();
					}
				}
			};

			// Store features IDs by level
			map._indoorFeaturesIDs = { pictures: {}, sequences: {} };
			map.on("sourcedata", LEVEL_SEM_LAYER, e => {
				if(e.sourceId === SEM_SRC_ID) {
					e.features.forEach(f => {
						if(!map._indoorFeaturesIDs.pictures[f.properties["osm|level"]]) {
							map._indoorFeaturesIDs.pictures[f.properties["osm|level"]] = new Set();
						}
						if(!map._indoorFeaturesIDs.sequences[f.properties["osm|level"]]) {
							map._indoorFeaturesIDs.sequences[f.properties["osm|level"]] = new Set();
						}
						map._indoorFeaturesIDs.pictures[f.properties["osm|level"]].add(f.properties.picid);
						map._indoorFeaturesIDs.sequences[f.properties["osm|level"]].add(f.properties.seqid);
					});
					updateIndoorLevelFilteringThrottled();
				}
			});

			// Create semantic overlay layer for loading with-level pictures
			const postMapLoad = () => {
				map.addSemanticOverlay?.(
					LEVEL_SEM_LAYER,
					"\"semantics.osm|level\" IS NOT NULL",
					{
						type: "circle",
						layout: { visibility: "visible" },
						paint: { "circle-opacity": 0 }
					}
				);

				// Load level from component/URL if any
				if(forceSwitchOn && map._parent._initParams?.getMapPostInit()?.level === DISABLED_LEVEL) {
					map.indoorEqual.setLevel("0");
				}
				else if(![null, undefined, ""].includes(map._parent._initParams?.getMapPostInit()?.level)) {
					map.indoorEqual.setLevel(map._parent._initParams?.getMapPostInit()?.level);
				}
			};
			// Wait for map load, done with loop due to an issue with styleload event not sent properly
			if(map.isStyleLoaded()) { postMapLoad(); }
			else {
				const waitForStyle = setInterval(() => {
					if(map.isStyleLoaded()) {
						postMapLoad();
						clearInterval(waitForStyle);
					}
				}, 250);
			}

			// Add map widget for level select
			map._parent.grid.appendChild(createWebComp("pnx-level-select", {
				slot: "bottom-right",
				_parent: map._parent,
				class: "pnx-only-map pnx-print-hidden",
			}));

			// Filter indoor pictures based on selected level
			let updateIndoorLevelFilteringThrottler;
			const updateIndoorLevelFiltering = () => {
				// Disabled = show everything
				if(map.indoorEqual.level === DISABLED_LEVEL) {
					map.restrictVisibleFeatures("hide", "pictures", []);
					map.restrictVisibleFeatures("hide", "sequences", []);
				}
				// Ground level = hide all other levels features
				else if(map.indoorEqual.level === "0") {
					// Pictures
					const nonGroundPics = new Set();
					Object.entries(map._indoorFeaturesIDs.pictures).forEach(([lvl, features]) => {
						if(lvl != "0") { features.forEach(f => nonGroundPics.add(f)); }
					});
					map.restrictVisibleFeatures("hide", "pictures", [...nonGroundPics]);

					// Sequences
					let nonGroundSeqs = new Set();
					Object.entries(map._indoorFeaturesIDs.sequences).forEach(([lvl, features]) => {
						if(lvl != "0") {
							nonGroundSeqs = nonGroundSeqs.union(features);
						}
					});
					if(map._indoorFeaturesIDs.sequences["0"]) { nonGroundSeqs = nonGroundSeqs.difference(map._indoorFeaturesIDs.sequences["0"]); } // To skip multi-level sequences
					map.restrictVisibleFeatures("hide", "sequences", [...nonGroundSeqs]);
				}
				// Other level = show only appropriate level features
				else {
					map.restrictVisibleFeatures(
						"keep",
						"pictures",
						map._indoorFeaturesIDs.pictures[map.indoorEqual.level]
							? [...map._indoorFeaturesIDs.pictures[map.indoorEqual.level]]
							: []
					);
					map.restrictVisibleFeatures(
						"keep",
						"sequences",
						map._indoorFeaturesIDs.sequences[map.indoorEqual.level]
							? [...map._indoorFeaturesIDs.sequences[map.indoorEqual.level]]
							: []
					);
				}
			};
			const updateIndoorLevelFilteringThrottled = () => {
				if(updateIndoorLevelFilteringThrottler) {
					clearTimeout(updateIndoorLevelFilteringThrottler);
					updateIndoorLevelFilteringThrottler = undefined;
				}
				updateIndoorLevelFilteringThrottler = setTimeout(updateIndoorLevelFiltering, 250);
			};

			map.indoorEqual.on("levelchange", updateIndoorLevelFilteringThrottled);
		}
		else {
			console.error("The indoor= plugin is not enabled. Please add the maplibre-gl-indoorequal before calling 'indoor' option of Panoramax Viewer.");
		}
	}
}
